import { useState, useRef, useEffect } from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Package } from "@/types";
import { usePackageStore } from "@/stores/packageStore";
import { useSupabaseAuth } from "@/contexts/SupabaseAuthContext";
import { toast } from "@/components/ui/sonner";
import { Pen, Eraser } from "lucide-react";

interface SignatureModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (id: number, signature: string, receiverName: string) => void;
  packageId?: number;
}

const SignatureModal = ({ 
  open, 
  onOpenChange, 
  onConfirm,
  packageId 
}: SignatureModalProps) => {
  const [receiverName, setReceiverName] = useState("");
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState<"pen" | "eraser">("pen");
  const [signature, setSignature] = useState<string | null>(null);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Setup canvas when component mounts
  useEffect(() => {
    if (open && canvasRef.current) {
      setupCanvas();
    }
  }, [open]);

  const handleSaveSignature = () => {
    if (!packageId) {
      toast.error("No package selected");
      return;
    }
    
    if (!receiverName.trim()) {
      toast.error("Please enter receiver name");
      return;
    }
    
    if (!signature) {
      toast.error("Please sign to confirm delivery");
      return;
    }
    
    // Pass signature and receiverName when marking delivered
    onConfirm(packageId, signature, receiverName);
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    setIsDrawing(true);
    const rect = canvas.getBoundingClientRect();
    // Use 'touches' in e to detect touch events (React SyntheticEvent compatibility)
    const x = ("touches" in e)
      ? (e as React.TouchEvent<HTMLCanvasElement>).touches[0].clientX - rect.left
      : (e as React.MouseEvent).clientX - rect.left;
    const y = ("touches" in e)
      ? (e as React.TouchEvent<HTMLCanvasElement>).touches[0].clientY - rect.top
      : (e as React.MouseEvent).clientY - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    // Always prevent default for touch events to avoid scrolling
    if ("touches" in e) {
      e.preventDefault();
    }
  };

  const continueDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    // Use 'touches' in e to detect touch events (React SyntheticEvent compatibility)
    const x = ("touches" in e)
      ? (e as React.TouchEvent<HTMLCanvasElement>).touches[0].clientX - rect.left
      : (e as React.MouseEvent).clientX - rect.left;
    const y = ("touches" in e)
      ? (e as React.TouchEvent<HTMLCanvasElement>).touches[0].clientY - rect.top
      : (e as React.MouseEvent).clientY - rect.top;
    if (tool === "pen") {
      ctx.lineTo(x, y);
      ctx.stroke();
    } else {
      ctx.clearRect(x - 10, y - 10, 20, 20);
    }
    // Always prevent default for touch events to avoid scrolling
    if ("touches" in e) {
      e.preventDefault();
    }
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    
    setIsDrawing(false);
    
    // Save the signature as data URL
    const canvas = canvasRef.current;
    if (canvas) {
      const dataUrl = canvas.toDataURL("image/png");
      setSignature(dataUrl);
    }
  };
  
  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignature(null);
  };
  
  const setupCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    
    // Set up the canvas for drawing
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0000ff"; // Blue color for signature
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" aria-describedby="signature-modal-desc">
        <span id="signature-modal-desc" style={{ display: 'none' }}>
          Sign to confirm package delivery or receipt.
        </span>
        <DialogHeader>
          <DialogTitle>Signature Confirmation</DialogTitle>
        </DialogHeader>
        
        <div className="mt-2">
          <p className="text-sm text-muted-foreground mb-4">
            Please sign to confirm package delivery
          </p>
          
          <div className="flex space-x-2 mb-4">
            <Button
              variant={tool === "pen" ? "default" : "outline"}
              size="sm"
              onClick={() => setTool("pen")}
              className="flex items-center"
            >
              <Pen className="h-4 w-4 mr-2" />
              Pen
            </Button>
            <Button
              variant={tool === "eraser" ? "default" : "outline"}
              size="sm"
              onClick={() => setTool("eraser")}
              className="flex items-center"
            >
              <Eraser className="h-4 w-4 mr-2" />
              Eraser
            </Button>
          </div>
          
          <div className="signature-pad-container mb-4">
            <canvas
              ref={canvasRef}
              width={400}
              height={150}
              className="border border-gray-300 rounded"
              onMouseDown={startDrawing}
              onMouseMove={continueDrawing}
              onMouseUp={stopDrawing}
              onMouseOut={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={continueDrawing}
              onTouchEnd={stopDrawing}
              onLoad={setupCanvas}
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              Name of receiver
            </label>
            <Input
              value={receiverName}
              onChange={(e) => setReceiverName(e.target.value)}
              placeholder="Enter name of person receiving the package"
            />
          </div>
          
          <p className="text-xs text-center text-muted-foreground mb-4">
            Sign above to confirm receipt of the package
          </p>
        </div>
        
        <DialogFooter className="flex justify-between">
          <Button
            variant="outline"
            onClick={clearCanvas}
            type="button"
          >
            Clear
          </Button>
          <div className="space-x-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              type="button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveSignature}
            >
              Confirm
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SignatureModal;
